import React, { useState, useCallback, useEffect } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon, Search, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import SignaturePad from "./SignaturePad";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { RegistrationSuccess } from "@/components/registration/RegistrationSuccess";
import { ExistingMemberModal } from "@/components/registration/ExistingMemberModal";
import { BancontactQRSection } from "@/components/registration/BancontactQRSection";
import {
  formConfig,
  abonnementOptionsWithDetails,
  statutMembreOptions,
  cleTCGOptions,
  dechargeOptions,
  paiementOptions,
  dechargeText,
  calculateEndDate,
  getPrice,
} from "@/data/mockData";

// ─── Small field components ──────────────────────────────────────────────────
const RequiredAsterisk = () => (
  <svg viewBox="0 0 7 7" fill="currentColor" width="7" height="7" className="wix-asterisk">
    <path d="M1.809 7 1 6.397 2.74 3.94 0 3.026l.31-.974L3 2.948V0h1v2.976l2.69-.924.31.975-2.719.934L6 6.397 5.19 7 3.503 4.609 1.809 7Z" />
  </svg>
);

const FieldLabel = ({ label, required }) => (
  <label className="wix-label">{label}{required && <RequiredAsterisk />}</label>
);

const TextInput = ({ label, placeholder, required, type = "text", value, onChange, icon, error }) => (
  <div className={`wix-field ${error ? "wix-field-error" : ""}`}>
    <FieldLabel label={label} required={required} />
    <div className={`wix-input-wrapper ${icon ? "wix-input-with-icon" : ""}`}>
      {icon === "search" && <div className="wix-input-icon"><Search size={20} strokeWidth={1.5} /></div>}
      <input type={type} className={`wix-input ${icon ? "wix-input-padded" : ""}`} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
    {error && <span className="wix-error-msg">Ce champ est obligatoire</span>}
  </div>
);

const DatePickerField = ({ label, required, value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`wix-field ${error ? "wix-field-error" : ""}`}>
      <FieldLabel label={label} required={required} />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="wix-input wix-date-input">
            <div className="wix-date-icon"><CalendarIcon size={20} strokeWidth={1.5} /></div>
            <span className={value ? "wix-date-value" : "wix-date-placeholder"}>
              {value ? format(value, "dd-MM-yyyy") : "Sélectionnez une date"}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value} onSelect={(date) => { onChange(date); setOpen(false); }} locale={fr} initialFocus />
        </PopoverContent>
      </Popover>
      {error && <span className="wix-error-msg">Ce champ est obligatoire</span>}
    </div>
  );
};

const DropdownField = ({ label, placeholder, required, options, value, onChange, error }) => (
  <div className={`wix-field ${error ? "wix-field-error" : ""}`}>
    <FieldLabel label={label} required={required} />
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="wix-select-trigger"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
    </Select>
    {error && <span className="wix-error-msg">Ce champ est obligatoire</span>}
  </div>
);

const RadioField = ({ label, required, options, value, onChange, error }) => (
  <div className={`wix-field ${error ? "wix-field-error" : ""}`}>
    <FieldLabel label={label} required={required} />
    <RadioGroup value={value} onValueChange={onChange} className="wix-radio-group">
      {options.map((opt) => (
        <label key={opt} className={`wix-radio-option ${value === opt ? "wix-radio-selected" : ""}`}>
          <RadioGroupItem value={opt} className="wix-radio-item" />
          <span className="wix-radio-label">{opt}</span>
        </label>
      ))}
    </RadioGroup>
    {error && <span className="wix-error-msg">Ce champ est obligatoire</span>}
  </div>
);

const DechargeTextBlock = () => (
  <div className="wix-field wix-text-block">
    <p><strong>{dechargeText.title}</strong></p><p>&nbsp;</p>
    <p>{dechargeText.intro}</p><p>&nbsp;</p>
    <p><strong>{dechargeText.company}</strong> {dechargeText.companyAddress}</p>
    <p>{dechargeText.companyNumber} {dechargeText.companyRef}</p><p>et</p><p>&nbsp;</p>
    <p>{dechargeText.memberRef}</p><p>&nbsp;</p>
    <p><strong>{dechargeText.objectTitle}</strong></p><p>{dechargeText.objectText}</p><p>&nbsp;</p>
    <p>{dechargeText.declarationIntro}</p>
    {dechargeText.declarations.map((d, i) => <p key={i}>{i + 1}. {d}</p>)}
    <p>&nbsp;</p>
    <p><strong>{dechargeText.assuranceTitle}</strong> {dechargeText.assuranceText}</p><p>&nbsp;</p>
    <p>{dechargeText.nonAcceptText}</p><p>&nbsp;</p>
    <p><strong>{dechargeText.dureeTitle}</strong> {dechargeText.dureeText}</p>
    <p><strong>{dechargeText.diversTitle}</strong> {dechargeText.diversText}</p>
  </div>
);

// ─── Catalog helpers ─────────────────────────────────────────────────────────
const STATUS_TO_CATEGORY = {
  "Simple": "STANDARD", "Etudiant": "ETUDIANT", "Senior": "SENIOR",
  "Famille": "FAMILIAL", "Wolvendael / Staff Communal / Membre Bsports Tennis": "STANDARD",
};

const parseDurationMonths = (desc) => {
  if (!desc) return null;
  const m = desc.match(/(\d+)\s*mois/i);
  return m ? parseInt(m[1]) : null;
};

// ─── Main component ──────────────────────────────────────────────────────────
const RegistrationForm = () => {
  const { user, logout } = useAuth();
  const [catalogPlans, setCatalogPlans] = useState([]);
  const [formData, setFormData] = useState({
    prenom: "", nom: "", email: "", telephone: "", adresse: "",
    dateInscription: null, dateFinAbonnement: null,
    choixAbonnement: "", statutMembre: "", cleTCG: "",
    conditionSpecifique: "", siDomiciliation: "", compteBancaire: "",
    montantPayer: "", decharge: "", acceptConditions: false,
    modePaiement: "", dateSignature: null,
    signature: "", uploadedFile: null,
    mutuelle: "Aucune demande de Mutuelle",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [existingMember, setExistingMember] = useState(null);
  const [showExistingMemberModal, setShowExistingMemberModal] = useState(false);
  const [pendingSendMail, setPendingSendMail] = useState(true);
  const [isExistingMemberUpdate, setIsExistingMemberUpdate] = useState(false);

  // Load catalog
  useEffect(() => {
    apiClient.get("/catalog").then(res => setCatalogPlans(Array.isArray(res.data) ? res.data : res.data.items || [])).catch(() => {});
  }, []);

  const abonnementOptions = React.useMemo(() => {
    const unique = [...new Set(catalogPlans.map(p => p.name))];
    abonnementOptionsWithDetails.forEach(a => { if (!unique.includes(a.label)) unique.push(a.label); });
    return unique;
  }, [catalogPlans]);

  const getCatalogPrice = useCallback((planName, memberStatus) => {
    if (!planName || !memberStatus || !catalogPlans.length) return null;
    const cat = STATUS_TO_CATEGORY[memberStatus];
    let m = cat ? catalogPlans.find(p => p.name === planName && p.category === cat) : null;
    if (!m) m = catalogPlans.find(p => p.name === planName && (p.category === "PRIO_PACK" || p.category === "SINGLE_ENTRY"));
    if (!m) return null;
    return (m.payment_type === "DOMICILIATION" || m.duration_desc?.toLowerCase().includes("domiciliation")) ? `${m.price}€/mois` : `${m.price}€`;
  }, [catalogPlans]);

  const getCatalogEndDate = useCallback((startDate, planName) => {
    if (!startDate || !planName || !catalogPlans.length) return null;
    const match = catalogPlans.find(p => p.name === planName);
    if (!match) return null;
    const months = parseDurationMonths(match.duration_desc);
    if (!months) return null;
    const end = new Date(startDate);
    end.setMonth(end.getMonth() + months);
    return end;
  }, [catalogPlans]);

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: false }));
  }, [errors]);

  // Auto-calculate end date
  useEffect(() => {
    if (formData.dateInscription && formData.choixAbonnement) {
      const end = getCatalogEndDate(formData.dateInscription, formData.choixAbonnement) || calculateEndDate(formData.dateInscription, formData.choixAbonnement);
      if (end) setFormData(prev => ({ ...prev, dateFinAbonnement: end }));
    }
  }, [formData.dateInscription, formData.choixAbonnement, getCatalogEndDate]);

  // Auto-calculate price
  useEffect(() => {
    if (formData.choixAbonnement && formData.statutMembre) {
      const price = getCatalogPrice(formData.choixAbonnement, formData.statutMembre) || getPrice(formData.choixAbonnement, formData.statutMembre);
      if (price) setFormData(prev => ({ ...prev, montantPayer: price }));
    }
  }, [formData.choixAbonnement, formData.statutMembre, getCatalogPrice]);

  const validateForm = () => {
    const required = {
      prenom: formData.prenom, nom: formData.nom, email: formData.email,
      telephone: formData.telephone, adresse: formData.adresse,
      dateInscription: formData.dateInscription, dateFinAbonnement: formData.dateFinAbonnement,
      choixAbonnement: formData.choixAbonnement, statutMembre: formData.statutMembre,
      cleTCG: formData.cleTCG, montantPayer: formData.montantPayer,
      decharge: formData.decharge, acceptConditions: formData.acceptConditions,
      modePaiement: formData.modePaiement, dateSignature: formData.dateSignature,
      signature: formData.signature,
    };
    const newErrors = {};
    Object.entries(required).forEach(([k, v]) => { if (!v) newErrors[k] = true; });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      document.querySelector(".wix-field-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    return Object.keys(newErrors).length === 0;
  };

  const formatDateForApi = (d) => {
    if (!d) return null;
    if (typeof d === "string") return d.slice(0, 10);
    if (d instanceof Date) return d.toISOString().slice(0, 10);
    return null;
  };

  const buildPayload = () => ({
    prenom: formData.prenom, nom: formData.nom, email: formData.email,
    telephone: formData.telephone, adresse: formData.adresse,
    choixAbonnement: formData.choixAbonnement, statutMembre: formData.statutMembre,
    montantPayer: formData.montantPayer, modePaiement: formData.modePaiement,
    dateInscription: formatDateForApi(formData.dateInscription),
    dateFinAbonnement: formatDateForApi(formData.dateFinAbonnement),
    cleTCG: formData.cleTCG, conditionSpecifique: formData.conditionSpecifique,
    siDomiciliation: formData.siDomiciliation, compteBancaire: formData.compteBancaire,
    decharge: formData.decharge, formulaireAide: user?.name || user?.email || "",
    signature: formData.signature, acceptConditions: formData.acceptConditions,
    mutuelle: formData.mutuelle,
  });

  const checkExistingMember = async () => {
    try {
      const res = await apiClient.get("/members/check-exists", {
        params: { nom: formData.nom, prenom: formData.prenom, email: formData.email }, timeout: 30000,
      });
      return res.data;
    } catch (err) { console.error("Error checking existing member:", err); return { exists: false }; }
  };

  const handleSubmit = async (e) => { e.preventDefault(); if (!validateForm()) return; await handleSubmitWithCheck(true); };
  const handleSaveWithoutEmail = async () => { if (!validateForm()) return; await handleSubmitWithCheck(false); };

  const handleSubmitWithCheck = async (sendMail) => {
    setIsSubmitting(true);
    try {
      const checkResult = await checkExistingMember();
      if (checkResult.exists) {
        setExistingMember(checkResult); setPendingSendMail(sendMail);
        setShowExistingMemberModal(true); setIsSubmitting(false); return;
      }
      await submitRegistration(sendMail, false);
    } catch (err) {
      console.error("Error during submission:", err);
      await submitRegistration(sendMail, false);
    }
  };

  const handleConfirmExistingMember = async () => {
    setShowExistingMemberModal(false); setIsSubmitting(true);
    await submitRegistration(pendingSendMail, true);
  };

  const submitRegistration = async (sendMail, isUpdate = false, retryCount = 0) => {
    setIsSubmitting(true);
    try {
      const response = await apiClient.post(`/registration?send_mail=${sendMail}`, buildPayload(), { timeout: 60000, headers: { "Content-Type": "application/json" } });
      setEmailSent(!!(sendMail && response.data?.email_sent));
      setIsExistingMemberUpdate(response.data?.is_existing_member || isUpdate);
      setSubmitted(true);
    } catch (err) {
      console.error("Error submitting form:", err);
      let errorMessage = "Erreur inconnue";
      let shouldReconnect = false;
      const isAuthError = err.response?.status === 401 || err.response?.data?.detail === "Not authenticated" || (typeof err.response?.data?.detail === "string" && err.response?.data?.detail?.includes("Token"));
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        errorMessage = "La requête a pris trop de temps. L'inscription a peut-être réussi. Vérifiez dans le CRM.";
      } else if (err.message === "Network Error") {
        if (retryCount < 2) { setIsSubmitting(false); await new Promise(r => setTimeout(r, 1000)); return submitRegistration(sendMail, isUpdate, retryCount + 1); }
        errorMessage = "Erreur de connexion réseau après plusieurs tentatives.";
      } else if (isAuthError && retryCount < 1) {
        try { const r = await apiClient.post("/auth/refresh"); if (r.data?.access_token) { setIsSubmitting(false); return submitRegistration(sendMail, isUpdate, retryCount + 1); } } catch { /* pass */ }
        errorMessage = "Votre session a expiré. Veuillez vous reconnecter."; shouldReconnect = true;
      } else if (isAuthError) {
        errorMessage = "Votre session a expiré. Veuillez vous reconnecter."; shouldReconnect = true;
      } else if (err.response?.data?.detail) { errorMessage = err.response.data.detail; }
      else if (err.message) { errorMessage = err.message; }
      if (shouldReconnect) {
        if (window.confirm(`${errorMessage}\n\nCliquez OK pour vous reconnecter.`)) { logout(); window.location.href = "/login"; }
      } else { alert(`Une erreur s'est produite lors de l'inscription:\n${errorMessage}`); }
    } finally { setIsSubmitting(false); }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  if (submitted) return <RegistrationSuccess formData={formData} emailSent={emailSent} isExistingMemberUpdate={isExistingMemberUpdate} />;

  return (
    <div className="wix-form-container">
      <div className="wix-card">
        <form onSubmit={handleSubmit} noValidate>
          <div className="wix-header">
            <div className="wix-brand-logo">
              <img src="https://customer-assets.emergentagent.com/job_wix-form-replica/artifacts/8c7fc1k3_logofinal-logo-full-color-rgb-900px-w-72ppi.png" alt="B.health® - Restart Your Body" className="wix-logo-img" />
            </div>
            <h2 className="wix-title">{formConfig.title}</h2>
            <p className="wix-description">{formConfig.description}</p>
          </div>
          <div className="wix-fields">
            <TextInput label="Prénom" placeholder="Saisissez votre prénom" required value={formData.prenom} onChange={(v) => updateField("prenom", v)} error={errors.prenom} />
            <TextInput label="Nom de famille" placeholder="Saisissez votre nom de famille" required value={formData.nom} onChange={(v) => updateField("nom", v)} error={errors.nom} />
            <TextInput label="E-mail" placeholder="Saisissez votre e‑mail" required type="email" value={formData.email} onChange={(v) => updateField("email", v)} error={errors.email} />
            <TextInput label="Numéro de téléphone" placeholder="Saisissez votre numéro de téléphone" required type="tel" value={formData.telephone} onChange={(v) => updateField("telephone", v)} error={errors.telephone} />
            <TextInput label="Adresse" placeholder="Saisissez votre adresse" required icon="search" value={formData.adresse} onChange={(v) => updateField("adresse", v)} error={errors.adresse} />
            <DropdownField label="Statut du membre" placeholder="Sélectionnez une option" required options={statutMembreOptions} value={formData.statutMembre} onChange={(v) => updateField("statutMembre", v)} error={errors.statutMembre} />
            <DropdownField label="Choix d'abonnement" placeholder="Abonnement" required options={abonnementOptions} value={formData.choixAbonnement} onChange={(v) => updateField("choixAbonnement", v)} error={errors.choixAbonnement} />
            <DatePickerField label="Date d'inscription" required value={formData.dateInscription} onChange={(v) => updateField("dateInscription", v)} error={errors.dateInscription} />
            <DatePickerField label="Date de fin d'abonnement (calculée automatiquement)" required value={formData.dateFinAbonnement} onChange={(v) => updateField("dateFinAbonnement", v)} error={errors.dateFinAbonnement} />
            <div className={`wix-field ${errors.montantPayer ? "wix-field-error" : ""}`}>
              <FieldLabel label="Montant à payer (calculé automatiquement)" required />
              <input type="text" className="wix-input bg-gray-50" placeholder="€" value={formData.montantPayer} onChange={(e) => updateField("montantPayer", e.target.value)} readOnly={!!(formData.choixAbonnement && formData.statutMembre)} />
              {formData.choixAbonnement && formData.statutMembre && <span className="text-xs text-[#577083] mt-1">Calculé selon l'abonnement et le statut</span>}
              {errors.montantPayer && <span className="wix-error-msg">Ce champ est obligatoire</span>}
            </div>
            <DropdownField label="Besoin d'une clé TCG" placeholder="Sélectionnez une option" required options={cleTCGOptions} value={formData.cleTCG} onChange={(v) => updateField("cleTCG", v)} error={errors.cleTCG} />
            <div className="wix-field">
              <FieldLabel label="Condition Spécifique?" />
              <textarea className="wix-textarea" placeholder="Saisissez votre texte ici" value={formData.conditionSpecifique} onChange={(e) => updateField("conditionSpecifique", e.target.value)} />
            </div>
            <TextInput label="Si domiciliation" placeholder="Montant de la domiciliation min 12 mois en €" value={formData.siDomiciliation} onChange={(v) => updateField("siDomiciliation", v)} />
            <TextInput label="Domiciliation? Nous l'activons pour minimum 12 mois sur votre compte:" placeholder="Compte Bancaire BE.. .... .... ...." value={formData.compteBancaire} onChange={(v) => updateField("compteBancaire", v)} />
            <DechargeTextBlock />
            <RadioField label='Concernant cette "décharge de responsabilité"' required options={dechargeOptions} value={formData.decharge} onChange={(v) => updateField("decharge", v)} error={errors.decharge} />
            <div className={`wix-field ${errors.uploadedFile ? "wix-field-error" : ""}`}>
              <FieldLabel label="Conditions Générales de vente" required />
              <div className="wix-file-upload">
                <label className="wix-file-upload-area">
                  <input type="file" className="hidden" onChange={(e) => { if (e.target.files[0]) updateField("uploadedFile", e.target.files[0]); }} accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.rtf,.odt" />
                  <div className="wix-file-upload-content">
                    {formData.uploadedFile ? (<><Check size={23} /><span>{formData.uploadedFile.name}</span></>) : (
                      <><svg viewBox="0 0 23 23" fill="currentColor" width="23" height="23"><path d="M11.026 11L11 0h.974L12 11h11v1H12l-.026 11H11l.026-11H0v-1z" /></svg><span>Sélectionner un fichier</span></>
                    )}
                  </div>
                </label>
              </div>
            </div>
            <div className={`wix-field wix-checkbox-field ${errors.acceptConditions ? "wix-field-error" : ""}`}>
              <div className="flex items-start gap-3">
                <Checkbox checked={formData.acceptConditions} onCheckedChange={(v) => updateField("acceptConditions", v)} className="wix-checkbox mt-1" />
                <label className="wix-label wix-checkbox-label">J'accepte les "Conditions générales de vente" du centre Bsports Health.<RequiredAsterisk /></label>
              </div>
              {errors.acceptConditions && <span className="wix-error-msg" style={{ marginLeft: "32px" }}>Vous devez accepter les conditions</span>}
            </div>
            <div className="wix-field">
              <FieldLabel label="Fiche remplie par" />
              <div className="wix-input bg-gray-50" style={{ padding: "10px 12px", color: "#162d3d", fontWeight: 500 }}>{user?.name || user?.email || "Utilisateur connecté"}</div>
            </div>
            <DropdownField label="Votre mode de paiement" placeholder="Sélectionnez une option" required options={paiementOptions} value={formData.modePaiement} onChange={(v) => updateField("modePaiement", v)} error={errors.modePaiement} />
            <DropdownField label="Demande de remboursement mutuelle" placeholder="Sélectionnez votre mutuelle" options={["Aucune demande de Mutuelle", "Partena Mut", "Mutualite Libre Mutplus", "Mutualia", "Mutualite Neutre", "Solidaris"]} value={formData.mutuelle} onChange={(v) => updateField("mutuelle", v)} />
            <BancontactQRSection />
            <DatePickerField label="Date de la signature" required value={formData.dateSignature} onChange={(v) => updateField("dateSignature", v)} error={errors.dateSignature} />
            <div className={`wix-field ${errors.signature ? "wix-field-error" : ""}`}>
              <div className="wix-signature-label"><span className="wix-label">Votre signature</span><span className="wix-asterisk-text">*</span></div>
              <SignaturePad onSignatureChange={(v) => updateField("signature", v)} />
              {errors.signature && <span className="wix-error-msg">La signature est obligatoire</span>}
            </div>
          </div>
          <div className="wix-submit-area">
            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
              <button type="submit" className="wix-submit-btn" disabled={isSubmitting} data-testid="submit-with-email-btn">
                {isSubmitting ? <><span className="wix-spinner" />Traitement en cours...</> : "Envoyer mail + copie client"}
              </button>
              <button type="button" onClick={handleSaveWithoutEmail} className="wix-submit-btn" disabled={isSubmitting} style={{ backgroundColor: "#577083" }} data-testid="submit-without-email-btn">
                {isSubmitting ? <><span className="wix-spinner" />Traitement en cours...</> : "Enregistrer sans envoyer"}
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className="wix-footer"><span className="wix-footer-text">B.health® — Tous droits réservés</span></div>
      <ExistingMemberModal open={showExistingMemberModal} onOpenChange={setShowExistingMemberModal} existingMember={existingMember} onConfirm={handleConfirmExistingMember} />
    </div>
  );
};

export default RegistrationForm;
